/**
 * Filter vocabulary for the Stories / Versions list.
 *
 * Kept apart from the store so the filter menus, the persistence layer and the
 * query builders can all agree on the same option lists without importing the
 * store itself.
 */

/**
 * Rows the Type filter can show. Folders are listed alongside documents.
 *
 * Node 53:61378 also asks for "Proposals (if possible)". There is no proposal
 * type in the data model, and it was confirmed out of scope — so this is two
 * options by decision, not by omission.
 */
export const DOCUMENT_TYPES = ["folders", "documents"] as const;
export type DocumentType = (typeof DOCUMENT_TYPES)[number];

export const DOCUMENT_TYPE_LABELS: Record<DocumentType, string> = {
  folders: "Folders",
  documents: "Documents",
};

/** Which timestamp the Date filter and the date sort keys refer to. */
export const DATE_FIELDS = ["createdAt", "updatedAt"] as const;
export type DateField = (typeof DATE_FIELDS)[number];

export const DATE_FIELD_LABELS: Record<DateField, string> = {
  createdAt: "Date created",
  updatedAt: "Last activity",
};

/**
 * Preset windows offered by the Date filter. `custom` carries explicit
 * from/to values instead of an offset.
 */
export const DATE_PRESETS = [
  "last7days",
  "last30days",
  "last3months",
  "lastYear",
  "custom",
] as const;
export type DatePreset = (typeof DATE_PRESETS)[number];

export const DATE_PRESET_LABELS: Record<DatePreset, string> = {
  last7days: "Last 7 days",
  last30days: "Last 30 days",
  last3months: "Last 3 months",
  lastYear: "Last year",
  custom: "Custom",
};

const PRESET_DAYS: Record<Exclude<DatePreset, "custom">, number> = {
  last7days: 7,
  last30days: 30,
  last3months: 90,
  lastYear: 365,
};

export type DateFilter = {
  field: DateField;
  preset: DatePreset | null;
  /** ISO strings, only meaningful when preset === "custom". */
  from: string | null;
  to: string | null;
};

export const DEFAULT_DATE_FILTER: DateFilter = {
  field: "updatedAt",
  preset: null,
  from: null,
  to: null,
};

/** Midnight UTC, `dayOffset` days from the given date. */
function startOfUtcDay(date: Date, dayOffset = 0): Date {
  const copy = new Date(date);
  copy.setUTCHours(0, 0, 0, 0);
  copy.setUTCDate(copy.getUTCDate() + dayOffset);
  return copy;
}

/**
 * Turns a DateFilter into the concrete from/to the API expects.
 *
 * Presets are resolved against `now` rather than stored as absolute dates, so a
 * remembered "Last 7 days" still means the last 7 days on the next visit rather
 * than the week the user first picked it.
 *
 * The window covers whole days, and that matters more than it looks: these
 * values go into the request AND into the key that identifies the result set.
 * Resolved to the millisecond, no two calls ever agreed — so every
 * recomputation looked like a different filter, which is how "load more" came
 * to replace the rows it should have appended to, and why nothing could hit a
 * warm cache. A day is the smallest unit that keeps them equal for as long as
 * the user is looking at the list.
 *
 * The window ends at tomorrow's midnight so that "the last 7 days" includes
 * today, and is measured in UTC so it does not depend on where the browser is.
 */
export function resolveDateFilter(
  filter: DateFilter,
  now: Date = new Date(),
): { dateField: DateField; dateFrom?: string; dateTo?: string } | null {
  if (!filter.preset) return null;

  if (filter.preset === "custom") {
    if (!filter.from && !filter.to) return null;
    return {
      dateField: filter.field,
      ...(filter.from ? { dateFrom: filter.from } : {}),
      ...(filter.to ? { dateTo: filter.to } : {}),
    };
  }

  return {
    dateField: filter.field,
    dateFrom: startOfUtcDay(now, -PRESET_DAYS[filter.preset]).toISOString(),
    dateTo: startOfUtcDay(now, 1).toISOString(),
  };
}

/** Sort keys offered by the sort menu, in the order the design lists them. */
export const SORT_KEYS = [
  "name",
  "owner",
  "status",
  "versions",
  "readers",
  "createdAt",
  "updatedAt",
] as const;
export type SortKey = (typeof SORT_KEYS)[number];

export const SORT_KEY_LABELS: Record<SortKey, string> = {
  name: "Name",
  owner: "Owner",
  status: "Status",
  versions: "Versions",
  readers: "Readers",
  createdAt: "Date created",
  updatedAt: "Last activity",
};

/**
 * Versions and Readers are counts the documents themselves don't carry — they
 * come from separate whole-org lookups the list already loads. They are sorted
 * in the browser; everything else is sorted by the API.
 */
export const CLIENT_SORTED_KEYS: SortKey[] = ["versions", "readers"];

export function isClientSortedKey(key: string): key is SortKey {
  return (CLIENT_SORTED_KEYS as string[]).includes(key);
}
